import { useState, useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthState } from '@/components/feature/AuthGuard';
import { useQiRole } from '../lib/useQiRole';

interface OgNavLink {
  to: string;
  label: string;
  icon: string;
  show: boolean;
}

export default function OgShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const auth = useAuthState();
  const { role } = useQiRole(auth.user);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const links: OgNavLink[] = [
    { to: '/og/portal', label: '一之門簡介', icon: 'ri-flashlight-line', show: true },
    { to: '/og/qi-healing', label: '宇炁療育', icon: 'ri-heart-pulse-line', show: true },
    { to: '/og/qi-healing/intake', label: '服務登錄', icon: 'ri-calendar-check-line', show: true },
    { to: '/og/qi-healing/witnesses', label: '見證牆', icon: 'ri-eye-line', show: true },
    { to: '/og/qi-healing', label: '我的紀錄', icon: 'ri-user-line', show: !!auth.user },
    { to: '/og/qi-healing/new', label: '建立案例', icon: 'ri-add-line', show: role === 'practitioner' || role === 'candidate' },
    { to: '/og/qi-healing/review', label: '覆核後台', icon: 'ri-shield-check-line', show: role === 'admin' || role === 'reviewer' },
  ];

  const visibleLinks = links.filter((l) => l.show);

  const headerBg = scrolled
    ? 'bg-background-50/95 backdrop-blur-sm border-b border-foreground-100/60'
    : 'bg-transparent border-b border-transparent';

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}>
        <nav className="w-full px-4 md:px-6 flex items-center justify-between h-14">
          <Link to="/og/portal" className="flex items-center gap-2.5 whitespace-nowrap cursor-pointer">
            <span className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center">
              <i className="ri-flashlight-line text-sm text-background-50" />
            </span>
            <span className="text-base tracking-wide text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
              OG 一之門
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-0.5">
            {visibleLinks.map((link) => {
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to + link.label}
                  to={link.to}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer ${
                    active
                      ? 'bg-foreground-100 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-800 hover:bg-foreground-50'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            {auth.user ? (
              <button
                onClick={auth.signOut}
                className="hidden md:inline-flex whitespace-nowrap px-3 py-1.5 rounded-md border border-foreground-200 text-foreground-500 text-xs hover:bg-foreground-50 transition-colors cursor-pointer"
              >
                登出
              </button>
            ) : (
              <Link
                to={`/account?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer"
              >
                登入 AUM1001
              </Link>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-8 h-8 flex items-center justify-center text-foreground-600 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
            </button>
          </div>
        </nav>

        {mobileOpen && (
          <div className="md:hidden border-t border-foreground-100/60 bg-background-50 px-4 py-3 flex flex-col gap-0.5">
            {visibleLinks.map((link) => {
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to + link.label}
                  to={link.to}
                  className={`whitespace-nowrap px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer flex items-center gap-2 ${
                    active
                      ? 'bg-foreground-100 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-800 hover:bg-foreground-50'
                  }`}
                >
                  <i className={`${link.icon} text-base`} />
                  {link.label}
                </Link>
              );
            })}
            {auth.user ? (
              <button
                onClick={auth.signOut}
                className="whitespace-nowrap px-3 py-2.5 rounded-md text-sm text-left text-foreground-500 hover:bg-foreground-50 transition-colors cursor-pointer"
              >
                登出
              </button>
            ) : (
              <Link
                to={`/account?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                className="whitespace-nowrap px-3 py-2.5 rounded-md text-sm bg-accent-500 text-background-50 text-center"
              >
                登入 AUM1001
              </Link>
            )}
          </div>
        )}
      </header>

      <main className="pt-20 pb-20">{children}</main>

      <footer className="border-t border-foreground-100/60 bg-background-100">
        <div className="w-full px-4 md:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-400">OG 一之門 &middot; 宇炁療育登錄系統</p>
          <p className="text-xs text-foreground-400">
            所有內容以實際紀錄與本人回報為準，不由 AI 判定療效，亦不能取代醫療診斷。
          </p>
        </div>
      </footer>
    </div>
  );
}